import {
  all,
  call,
  getContext,
  put,
  select,
  takeEvery,
} from "redux-saga/effects";

import { CONTEXT } from "@const/redux";
import {
  blockCreatorFailure,
  blockCreatorSuccess,
  followCreatorFailure,
  followCreatorSuccess,
  getCreatorProfileFailure,
  getCreatorProfileSuccess,
  unblockCreatorFailure,
  unblockCreatorSuccess,
  unfollowCreatorFailure,
  unfollowCreatorSuccess,
  getCreatorProfile as getCreator,
  dontShowReportFailure,
  dontShowReportSuccess,
} from "./actions";
import { getSubscription } from "../subscriptions/actions";
import {
  blockCreatorActionTypes,
  followCreatorActionTypes,
  getCreatorProfileActionTypes,
  hideReportActionTypes,
  unblockCreatorActionTypes,
  unfollowCreatorActionTypes,
} from "./actionTypes";
import { deleteUserFromBlockListSuccess } from "../blockList/actions";
import { blockAuthor, unblockAuthor } from "../authors/actions";
import { getProfile } from "../profile/actions";
import { getMyPromoters, getPromotersById } from "../promoters/actions";
import { getMySupporters, getSupportersById } from "../supporters/actions";
import {
  getFollowersById,
  getFollowingsById,
  getMyFollowers,
  getMyFollowings,
} from "../followers/actions";
import { followersListLimit, followingListLimit } from "@const/limits";
import { localstorageKeys } from "@const/store";
import { removeEmptyKeys } from "@helpers/common";
import { getPosts } from "../posts/actions";
import { paginationLimit } from "@const/feed";

const getCreatorProfileAsync = ({ UsersService, username }) => {
  if (!UsersService) {
    console.log("Required services does not exist");

    return null;
  }

  return UsersService.getUserByUsername(username);
};

function* getCreatorProfile({ payload }) {
  try {
    const { username } = payload;
    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    const { data } = yield call(getCreatorProfileAsync, {
      UsersService,
      username,
    });

    yield put(getCreatorProfileSuccess(data));
  } catch ({ error }) {
    yield put(getCreatorProfileFailure(error));
  }
}

const blockCreatorAsync = ({ UsersService, payload }) => {
  if (!UsersService) {
    return;
  }

  return UsersService.blockUserByUserId(payload);
};

function* blockCreator({ payload }) {
  try {
    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    yield call(blockCreatorAsync, { UsersService, payload });

    yield all([put(blockCreatorSuccess(payload)), put(blockAuthor(payload))]);
  } catch ({ error }) {
    yield put(blockCreatorFailure(error));
  }
}

const unblockCreatorAsync = ({ UsersService, payload }) => {
  if (!UsersService) {
    return;
  }

  return UsersService.unblockUserByUserId(payload);
};
function* fetchProfileData(payload) {
  const {
    isMyProfile,
    isSupporter,
    isPromoter,
    isFollowers,
    isFollowings,
    userProfileId,
  } = payload;

  const fetchArgs = {
    id: isMyProfile ? null : userProfileId,
    limit: followersListLimit,
    offset: 0,
  };

  if (isFollowings) {
    yield put(isMyProfile ? getMyFollowings() : getFollowingsById(fetchArgs));
  }
  if (isFollowers) {
    yield put(isMyProfile ? getMyFollowers() : getFollowersById(fetchArgs));
  }
  if (isSupporter) {
    yield put(isMyProfile ? getMySupporters() : getSupportersById(fetchArgs));
  }
  if (isPromoter) {
    yield put(isMyProfile ? getMyPromoters() : getPromotersById(fetchArgs));
  }
}

function* unblockCreator({ payload }) {
  try {
    const { userId } = payload;

    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    yield call(unblockCreatorAsync, {
      UsersService,
      payload: { userId },
    });

    const { id, myUsername } = yield select(
      ({ creatorProfileReducer }) => creatorProfileReducer,
    );

    // todo: add hasSubscription check after BE update
    if (id == payload.userId) {
      yield put(getSubscription({ myUsername, limit: 3 }));
    }

    yield all([
      put(unblockCreatorSuccess(payload)),
      put(unblockAuthor(payload)),
      put(deleteUserFromBlockListSuccess(payload)),
    ]);

    yield call(fetchProfileData, payload);
  } catch ({ error }) {
    yield put(unblockCreatorFailure(error));
  }
}

const followCreatorAsync = ({ UsersService, id, values }) => {
  if (!UsersService) {
    console.log("Missing required service");
    return;
  }

  return UsersService.followCreator(id, values);
};

function* followCreator({ payload }) {
  try {
    const {
      shouldFetchUser,
      shouldFetchProfile,
      shouldFetchPosts,
      shouldFetchFollowingsList,
      ...restPayload
    } = payload;

    const { UsersService, StorageService } = yield getContext(CONTEXT.SERVICES);
    const trackingCode = StorageService.get(localstorageKeys.trackingCode, "");

    const requestPayload = {
      ...restPayload,
      values: removeEmptyKeys({
        ...restPayload.values,
        trackingCode,
      }),
    };

    const { data } = yield call(followCreatorAsync, {
      UsersService,
      ...requestPayload,
    });

    // todo: think about better solution
    if (shouldFetchUser && payload.username) {
      yield put(getCreator({ username: payload.username }));
    }
    if (shouldFetchProfile) {
      yield put(getProfile());
    }
    if (shouldFetchFollowingsList) {
      yield put(getMyFollowings({ limit: followingListLimit, offset: 0 }));
    }
    if (shouldFetchPosts && payload.username) {
      yield put(
        getPosts({
          username: payload.username,
          limit: paginationLimit,
        }),
      );
    }

    yield put(
      followCreatorSuccess({
        userId: payload.id,
        type: payload.type,
        id: data?.id,
        postId: payload?.postId,
      }),
    );
  } catch ({ error }) {
    yield put(followCreatorFailure(error));
  }
}

const unfollowCreatorAsync = ({ UsersService, id }) => {
  if (!UsersService) {
    console.log("Missing required service");

    return;
  }

  return UsersService.unfollowCreator(id);
};

function* unfollowCreator({ payload }) {
  try {
    const {
      shouldFetchUser,
      shouldFetchProfile,
      shouldFetchPosts,
      ...requestPayload
    } = payload;
    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    yield call(unfollowCreatorAsync, { UsersService, ...requestPayload });

    // todo: think about better solution
    if (shouldFetchUser && payload.username) {
      yield put(getCreator({ username: payload.username }));
    }
    if (shouldFetchProfile) {
      yield put(getProfile());
    }
    if (shouldFetchPosts && payload.username) {
      yield put(
        getPosts({
          username: payload.username,
          limit: paginationLimit,
        }),
      );
    }
    yield put(
      unfollowCreatorSuccess({ userId: payload.id, type: payload.type }),
    );
  } catch ({ error }) {
    yield put(unfollowCreatorFailure(error));
  }
}

const hideReportNotificationAsync = ({ UsersService, id }) => {
  if (!UsersService) {
    console.log("Missing required service");

    return;
  }

  return UsersService.dontShowReportNotification(id);
};

function* hideReportNotification({ payload }) {
  try {
    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    yield call(hideReportNotificationAsync, { UsersService, id: payload });

    yield put(dontShowReportSuccess());
  } catch ({ error }) {
    yield put(dontShowReportFailure(error));
  }
}

export function* watchGetcreatorProfile() {
  yield takeEvery(getCreatorProfileActionTypes.request, getCreatorProfile);
}

export function* watchBlockCreator() {
  yield takeEvery(blockCreatorActionTypes.request, blockCreator);
}

export function* watchUnblockCreator() {
  yield takeEvery(unblockCreatorActionTypes.request, unblockCreator);
}

export function* watchFollowCreator() {
  yield takeEvery(followCreatorActionTypes.request, followCreator);
}

export function* watchUnfollowCreator() {
  yield takeEvery(unfollowCreatorActionTypes.request, unfollowCreator);
}

export function* watchHideReportNotification() {
  yield takeEvery(hideReportActionTypes.request, hideReportNotification);
}
